import { isObject, Options } from 'highcharts';
import { useMediaQuery, useTheme } from '@mui/material';
import i18next from '../../i18n';

export enum RANGE_MODE {
  Daily,
  Monthly,
  Quarterly,
  Halfly,
  YTD,
  Yearly,
  All,
}

const hiddenOptions = (
  defaultOptions: Options,
  onLoad: any,
  xMax: number | undefined,
  customOptions?: Options
) => ({
  ...defaultOptions,
  plotOptions: {
    ...defaultOptions.plotOptions,
    ...customOptions?.plotOptions,
    series: {
      ...(defaultOptions.plotOptions || {}).series,
      animation: false,
      ...customOptions?.plotOptions?.series,
    },
  },
  chart: {
    ...defaultOptions.chart,
    // safari 15 styledmode fix
    // see https://issueexplorer.com/issue/highcharts/highcharts/16483
    spacingTop: 50,
    spacingLeft: 0,
    ...customOptions?.chart,
    events: {
      load: onLoad,
    },
  },
  legend: {
    enabled: true,
    ...customOptions?.legend,
  },

  navigator: {
    enabled: false,
  },
  scrollbar: {
    enabled: false,
  },
  exporting: {
    enabled: false,
    csv: {
      dateFormat: '%Y-%m-%d',
    },
  },
  rangeSelector: {
    height: 40,
    enabled: false,
  },
  yAxis: customOptions?.yAxis || defaultOptions.yAxis,
  xAxis: customOptions?.xAxis || {
    max: xMax,
  },
});

interface MainOptions {
  isFullScreen: boolean;
  onFullScreen: any;
  loginDialog: any;
  realTimeAvailable?: boolean;
  previewOnly?: boolean;
  buttonColor: string;
  plotMetric?: string;
  compareMode?: string;
  roundingDecimals?: number;
  disableMetrics?: boolean;
  pointFormatter: any;
  custom?: Options;
}

const setupMainOptions = (
  defaultOptions: Options,
  {
    isFullScreen,
    onFullScreen,
    realTimeAvailable = false,
    previewOnly,
    buttonColor,
    plotMetric = 'price_return',
    compareMode,
    disableMetrics = false,
    roundingDecimals = 2,
    pointFormatter,
    custom,
  }: MainOptions
) => {
  const menuItems = [
    'viewFullscreen',
    'printChart',
    'separator',
    'downloadPNG',
    'downloadJPEG',
    'downloadPDF',
    'downloadSVG',
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const leftMargin = isMobile ? 25 : 10;
  const yAxis = {
    yAxis: combine(defaultOptions?.yAxis || {}, custom?.yAxis || {}),
  };

  const subtitle = realTimeAvailable
    ? {
        subtitle: {
          text: i18next.t('chartSubtitle'),
          verticalAlign: 'bottom',
          y: -75,
        },
      }
    : {};

  const BaseChartbuttons = [
    {
      type: 'month',
      count: 1,
      text: '1m',
      title: i18next.t('view1Month'),
    },
    {
      type: 'month',
      count: 3,
      text: '3m',
      title: i18next.t('view3Months'),
    },
    {
      type: 'month',
      count: 6,
      text: '6m',
      title: i18next.t('view6Months'),
    },
    {
      type: 'ytd',
      text: 'YTD',
      title: i18next.t('viewYearToDate'),
    },
    {
      type: 'year',
      count: 1,
      text: i18next.t('1Y'),
      title: i18next.t('view1Year'),
    },
    {
      type: 'all',
      text: i18next.t('All'),
      title: i18next.t('viewAll'),
    },
  ];

  const opts = {
    ...defaultOptions,
    plotOptions: {
      ...defaultOptions.plotOptions,
      ...custom?.plotOptions,
    },

    chart: {
      ...defaultOptions.chart,
      spacingTop: 20,
      margin: previewOnly ? [10, 25, 30, leftMargin] : defaultOptions.chart?.margin,
      ...custom?.chart,
    },
    legend: {
      ...defaultOptions.legend,
      ...custom?.legend,
    },
    ...subtitle,
    navigator: {
      enabled: !previewOnly,
      ...custom?.navigator,
    },
    scrollbar: {
      enabled: !previewOnly,
      ...custom?.scrollbar,
    },
    rangeSelector: {
      ...defaultOptions.rangeSelector,
      height: 40,
      allButtonsEnabled: false,
      ...custom?.rangeSelector,
      buttons: custom?.rangeSelector?.buttons || { ...BaseChartbuttons },
      buttonTheme: {
        ...defaultOptions.rangeSelector?.buttonTheme,
        states: {
          hover: {
            fill: buttonColor,
          },
          select: {
            fill: buttonColor,
          },
        },
        // width: 'auto',
      },
    },
    exporting: {
      ...defaultOptions.exporting,
      enabled: !previewOnly,
      csv: {
        dateFormat: '%Y-%m-%d',
      },
      menuItemDefinitions: {
        ...(defaultOptions.exporting || {}).menuItemDefinitions,
        // Custom FullScreen Definition
        fullscreen: {
          ...((defaultOptions.exporting || {}).menuItemDefinitions || {}).fullscreen,
          onclick: onFullScreen,
          text: isFullScreen ? 'Exit Fullscreen' : 'Fullscreen',
        },
      },
      buttons: {
        ...(defaultOptions.exporting || {}).buttons,
        contextButton: {
          ...((defaultOptions.exporting || {}).buttons || {}).contextButton,
          // Custom downloadCSV Definition
          menuItems: menuItems,
        },
      },
      ...custom?.exporting,
    },
    tooltip: {
      ...defaultOptions.tooltip,
      pointFormatter: function () {
        if (pointFormatter) {
          return pointFormatter(this);
        }
        return compareMode === 'absolute'
          ? absolutePointFormatter(this, disableMetrics, plotMetric, roundingDecimals)
          : defaultPointFormatter(this, disableMetrics, plotMetric, roundingDecimals);
      },
      ...custom?.tooltip,
    },
    colorAxis: custom?.colorAxis,
    ...yAxis,
    xAxis: custom?.xAxis || { minRange: 1 },
  };

  return opts;
};
export { hiddenOptions, setupMainOptions };

const absolutePointFormatter = (
  point: any,
  disableMetrics: boolean,
  defaultMetric: string,
  roundingDecimals = 2
) => {
  const defaults = [defaultMetric, 'close'];
  const metricName = (point.series?.options?.metric || '').replaceAll('_', ' ');
  const metric = !defaults.includes(point.series?.options?.metric) ? `(${metricName})` : '';

  const value = Number(point.y).toFixed(roundingDecimals);

  return `<b>${point.series.name} ${metric}: ${value}</b>`;
};

const defaultPointFormatter = (
  point: any,
  disableMetrics: boolean,
  defaultMetric: string,
  roundingDecimals = 2
): string => {
  const defaults = [defaultMetric, 'close'];
  const plus = point.change > 0 ? '+' : '';
  const metricName = (point.series?.options?.metric || '').replaceAll('_', ' ');
  const metric =
    !disableMetrics && !defaults.includes(point.series?.options?.metric) ? `(${metricName})` : '';

  const value = Number(point.y).toFixed(roundingDecimals);
  const change = plus + Number(point.change).toFixed(roundingDecimals);

  return `<b>${point.series.name} ${metric}: ${value}</b> (${change}%)`;
};

type S<T> = T | T[];
function combine<T extends Record<string, any>>(s1: S<T>, s2: S<T>): S<T> {
  if (Array.isArray(s1) && Array.isArray(s2)) {
    // Merge two arrays without duplicates
    return Array.from(new Set([...s1, ...s2]));
  } else if (!isObject(s1) && !isObject(s2)) {
    // If both are not objects, return s2
    return s2;
  } else if (!Array.isArray(s1) && Array.isArray(s2)) {
    // s1 is an object, s2 is an array: merge s1 with s2[0] and return combined array
    if (s2.length === 0) {
      return s1; // If s2 is an empty array, return s1
    }
    const [firstOfS2, ...restOfS2] = s2;
    const combined = combine(s1, firstOfS2);
    return [...(Array.isArray(combined) ? combined : [combined]), ...restOfS2];
  } else if (Array.isArray(s1) && !Array.isArray(s2)) {
    // s1 is an array, s2 is an object: merge s2 with s1[0] and return combined array
    if (s1.length === 0) {
      return s2; // If s1 is an empty array, return s2
    }
    const [firstOfS1, ...restOfS1] = s1;
    const combined = combine(firstOfS1, s2);
    return [...(Array.isArray(combined) ? combined : [combined]), ...restOfS1];
  } else {
    // Both are objects: recursively merge their properties
    const combined: any = { ...s1 };
    for (const key in s2) {
      if (Object.prototype.hasOwnProperty.call(s2, key)) {
        if (Object.prototype.hasOwnProperty.call(combined, key)) {
          combined[key] = combine(combined[key], s2[key as keyof typeof s2]);
        } else {
          combined[key] = s2[key as keyof typeof s2];
        }
      }
    }
    return combined as T;
  }
}
